import type { PureJourney } from '@epilot/journey-logic-commons'
import { journeyStorage } from '@epilot/journey-logic-commons'
import type { JourneyId } from '@epilot/journey-logic-commons/src/types/next'

import {
  selectIsRootLauncherJourney,
  selectLaunchableJourneyIdsSorted
} from '../selectors/config'
import type { PreviewHydrateAction } from '../types/actions'
import { APP_STATUS } from '../types/enums'

import { actionFactory } from './factory'
import { featureFlagsChanged } from './featureFlagsChanged'
import { journeyChanged } from './journeyChanged'
import { previewThemeHydrate } from './previewThemeHydrate'

/**
 * Fake Journey Id used when previewing a Journey that doesn't have one (i.e. a template).
 * @deprecated since templates should be removed
 */
export const PREVIEW_JOURNEY_ID = 'fake-preview-id'

export const previewHydrate = actionFactory<PreviewHydrateAction>(
  (state, { token, selectedStepId, journey, featureFlags }) => {
    if (journey) {
      const _journey = journeyWithId(journey)
      const { design } = _journey as PureJourney

      journeyChanged({ journey: _journey }, state)

      if (design) {
        previewThemeHydrate(
          {
            theme: design.theme,
            logoUrl: design.logoUrl || '',
            designTokens: design.designTokens
          },
          state
        )
      }

      const prevRootJourneyId = state.rootJourneyId
      const prevSelectedJourneyId = state.selectedJourneyId

      state.rootJourneyId = _journey.journeyId as JourneyId

      // Triggers re-render of the App for Design Builder Journey selection
      // Only for non-launcher journeys
      if (
        prevRootJourneyId &&
        prevSelectedJourneyId &&
        prevSelectedJourneyId === prevRootJourneyId
      ) {
        state.selectedJourneyId = _journey.journeyId as JourneyId
      }
    }

    if (token) journeyStorage.setItem('token', token)

    featureFlagsChanged({ featureFlags }, state)

    const isLauncher = selectIsRootLauncherJourney(state)
    const launchableJourneys = selectLaunchableJourneyIdsSorted(state)
    const firstLaunchable = launchableJourneys[0] ?? null // to unselect if needed

    // if no journey is currently selected
    if (!state.selectedJourneyId) {
      // selects the first launchable or the root journey
      state.selectedJourneyId = isLauncher
        ? firstLaunchable
        : state.rootJourneyId
    } else if (
      // is launcher and selected journey is not part of linked journeys
      isLauncher &&
      !launchableJourneys.includes(state.selectedJourneyId)
    ) {
      state.selectedJourneyId = firstLaunchable
    }

    // select the initial step for non-launcher journeys only
    // TODO: remove when launcher-journeys are fully migrated to the new schema,
    // which will remove the concept of steps for them
    if (!isLauncher) {
      state.initialStepId = selectedStepId
    }

    state.appStatus = APP_STATUS.READY
  },
  'previewHydrate'
)

/**
 * Guarantees all journey objects have a Journey Id.
 * This is necessary because templates won't have a Journey Id but can still be previewed on the Wizard.
 *
 * @deprecated templates should be removed soon, in favour of blueprints
 */
export const journeyWithId = (
  journey: PreviewHydrateAction['payload']['journey']
) => {
  if (journey?.journeyId) return journey

  return {
    ...journey,
    journeyId: PREVIEW_JOURNEY_ID
  }
}
